import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Card,
  CardContent,
  Modal,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as DeclineIcon,
  Visibility as ViewIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon, // ADDED: Import MoreVertIcon
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import {
  fetchSafeRunPosts,
  approvePost,
  rejectPost,
} from "../../Action/approvalApi";
import PostPreviewModal from "./PostPreviewModal";
import { getAuthTokenPayload } from "../../Utils";

const SocialMediaApprovals = ({
  searchTerm,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  userPermissions,
  onPreviewOpen,
  onActionOpen,
  onMenuOpen,
  selectedItem,
  setSelectedItem,
  getStatusColor,
  onClearSearch,
  onStatsUpdate,
  isApprovalTimeValid, // ADDED
  getTimeValidationMessage, // ADDED
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [socialMediaPosts, setSocialMediaPosts] = useState([]);
  const [loadingSocialPosts, setLoadingSocialPosts] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  // Get current month in the correct format
  const getCurrentMonth = () => {
    const currentDate = new Date();
    return currentDate
      .toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
      .replace(" ", "-");
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [tempSelectedMonth, setTempSelectedMonth] = useState(getCurrentMonth());

  // Memoize available months to prevent recalculation on every render
  const availableMonths = useMemo(() => {
    const currentDate = new Date();
    const months = [];

    // Generate next 12 months starting from current month
    for (let i = 0; i < 12; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + i,
        1
      );
      const monthYear = date
        .toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        })
        .replace(" ", "-");

      const fullMonthName = date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });

      months.push({
        value: monthYear,
        label: fullMonthName,
        short: date.toLocaleDateString("en-US", { month: "short" }),
        year: date.getFullYear(),
        isCurrentMonth: i === 0,
      });
    }

    return months;
  }, []); // Empty dependency array - only calculate once

  // Extract hashtags from post content
  const extractHashtags = (content) => {
    if (!content) return [];
    const hashtags = content.match(/#\w+/g) || [];
    return hashtags.slice(0, 5); // Limit to 5 hashtags
  };

  // Fetch social media posts from API
  const fetchSocialMediaPosts = useCallback(async () => {
    try {
      setLoadingSocialPosts(true);
      setApiError(null);

      console.log("Fetching posts for month:", selectedMonth);

      // Get clientId from token
      const clientId = getAuthTokenPayload()?.clientId;

      // Convert month to proper date format
      const scheduleDateTime = selectedMonth;

      const result = await fetchSafeRunPosts(clientId, scheduleDateTime);

      console.log("API result:", result);

      if (result.success && result.data) {
        // Transform API data to match component structure
        const transformedPosts = result.data.posts.map((post, index) => ({
          id: `SM${(index + 1).toString().padStart(3, "0")}`,
          type: post.contentType || "Social Media",
          content: post.postContent,
          imageUrl: post.mediaUrls?.[0] || post.mediaFiles?.[0]?.s3Url,
          mediaUrls: post.mediaUrls || [],
          platform: post.platforms?.join(", ") || "Multi-platform",
          platforms: post.platforms || [],
          accounts: post.accounts || [],
          selectedAccounts: post.selectedAccounts || [],
          requestedBy: "System",
          requestedAt: new Date(post.CreatedAt).toLocaleString(),
          scheduledFor: new Date(post.scheduleDateTime).toLocaleString(),
          // Store both formatted and raw date for sorting
          scheduledDateTime: new Date(post.scheduleDateTime),
          rawScheduleDateTime: post.scheduleDateTime,
          status: post.requiresApproval ? "Pending" : "Approved",
          hashtags: extractHashtags(post.postContent),
          engagement: { likes: 0, comments: 0, shares: 0 },
          mediaFiles: post.mediaFiles || [],
          contentType: post.contentType,
          mediaFileCount:
            post.mediaUrls?.length || post.mediaFiles?.length || 0,
          updatedAt: post.UpdatedAt,
          createdAt: post.CreatedAt,
          requiresApproval: post.requiresApproval,
          originalScheduleDateTime: post.scheduleDateTime,
          accountNames:
            post.accounts?.map((acc) => acc.accountName).join(", ") || "",
        }));

        // Sort posts by scheduled date - nearest upcoming posts first
        const sortedPosts = transformedPosts.sort((a, b) => {
          const now = new Date();
          const dateA = a.scheduledDateTime;
          const dateB = b.scheduledDateTime;

          // First, prioritize by status - Pending posts first
          if (a.status === "Pending" && b.status !== "Pending") return -1;
          if (b.status === "Pending" && a.status !== "Pending") return 1;

          // Within same status, sort by proximity to current time
          const diffA = Math.abs(dateA.getTime() - now.getTime());
          const diffB = Math.abs(dateB.getTime() - now.getTime());

          // If both are in the future, show nearest first
          if (dateA >= now && dateB >= now) {
            return dateA.getTime() - dateB.getTime();
          }

          // If both are in the past, show most recent first
          if (dateA < now && dateB < now) {
            return dateB.getTime() - dateA.getTime();
          }

          // If one is future and one is past, prioritize future
          if (dateA >= now && dateB < now) return -1;
          if (dateB >= now && dateA < now) return 1;

          // Fallback to nearest to current time
          return diffA - diffB;
        });

        setSocialMediaPosts(sortedPosts);
        console.log("Transformed and sorted posts:", sortedPosts);
      } else {
        setApiError(result.message || "Failed to fetch social media posts");
        setSocialMediaPosts([]);
      }
    } catch (error) {
      console.error("Error fetching social media posts:", error);
      setApiError("Network error occurred while fetching posts");
      setSocialMediaPosts([]);
    } finally {
      setLoadingSocialPosts(false);
    }
  }, [selectedMonth]);

  // Handle post preview
  const handlePreviewClick = (post) => {
    setSelectedPost(post);
    setPreviewModalOpen(true);
  };

  // Handle post update after approve/reject
  const handlePostUpdated = (updatedPost) => {
    setSocialMediaPosts((prevPosts) =>
      prevPosts.map((post) => (post.id === updatedPost.id ? updatedPost : post))
    );
  };

  // Quick approve/reject functions for table buttons
  const handleQuickApprove = async (post) => {
    try {
      const clientId = getAuthTokenPayload()?.clientId;
      const scheduleDateTime =
        post.originalScheduleDateTime ||
        new Date(post.scheduledFor).toISOString();

      const result = await approvePost(scheduleDateTime, clientId);

      if (result.success) {
        // Update post status locally
        const updatedPost = { ...post, status: "Approved" };
        handlePostUpdated(updatedPost);
      } else {
        console.error("Failed to approve post:", result.message);
        // You could show a snackbar or alert here
      }
    } catch (error) {
      console.error("Error approving post:", error);
    }
  };

  const handleQuickReject = async (post) => {
    try {
      const clientId = getAuthTokenPayload()?.clientId;
      const scheduleDateTime =
        post.originalScheduleDateTime ||
        new Date(post.scheduledFor).toISOString();

      const result = await rejectPost(scheduleDateTime, clientId);

      if (result.success) {
        // Update post status locally
        const updatedPost = { ...post, status: "Rejected" };
        handlePostUpdated(updatedPost);
      } else {
        console.error("Failed to reject post:", result.message);
        // You could show a snackbar or alert here
      }
    } catch (error) {
      console.error("Error rejecting post:", error);
    }
  };

  // Fetch data when component mounts or selectedMonth changes
  useEffect(() => {
    console.log("useEffect triggered, selectedMonth:", selectedMonth); // Debug log
    fetchSocialMediaPosts();
  }, [fetchSocialMediaPosts]);

  const handleRefreshPosts = () => {
    console.log("Manual refresh triggered"); // Debug log
    fetchSocialMediaPosts();
  };

  // Month picker functions using Modal instead of Popover
  const handleMonthPickerOpen = useCallback(() => {
    setTempSelectedMonth(selectedMonth); // Set temp selection to current selection
    setMonthPickerOpen(true);
  }, [selectedMonth]);

  const handleMonthPickerClose = useCallback(() => {
    setMonthPickerOpen(false);
  }, []);

  // Updated to only set temporary selection
  const handleMonthSelect = useCallback((monthValue) => {
    console.log("Month selected in modal:", monthValue); // Debug log
    setTempSelectedMonth(monthValue); // Only update temporary selection
    // Modal stays open
  }, []);

  // Apply selection and close modal
  const handleApplyMonth = useCallback(() => {
    console.log("Applying month selection:", tempSelectedMonth); // Debug log
    setSelectedMonth(tempSelectedMonth); // Apply the temporary selection
    setMonthPickerOpen(false); // Close the modal
  }, [tempSelectedMonth]);

  // Cancel selection and close modal
  const handleCancelMonth = useCallback(() => {
    setTempSelectedMonth(selectedMonth); // Reset to original selection
    setMonthPickerOpen(false); // Close the modal
  }, [selectedMonth]);

  // Navigation functions
  const currentIndex = availableMonths.findIndex(
    (m) => m.value === selectedMonth
  );

  const handlePrevMonth = () => {
    if (currentIndex > 0) {
      const newMonth = availableMonths[currentIndex - 1].value;
      console.log("Previous month selected:", newMonth); // Debug log
      setSelectedMonth(newMonth);
    }
  };

  const handleNextMonth = () => {
    if (currentIndex < availableMonths.length - 1) {
      const newMonth = availableMonths[currentIndex + 1].value;
      console.log("Next month selected:", newMonth); // Debug log
      setSelectedMonth(newMonth);
    }
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    const filtered = socialMediaPosts.filter(
      (item) =>
        item.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.requestedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.platform?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.hashtags?.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        item.accountNames?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Additional smart sorting after filtering
    return filtered.sort((a, b) => {
      const now = new Date();
      const dateA = a.scheduledDateTime;
      const dateB = b.scheduledDateTime;

      // First priority: Pending posts that are upcoming
      const aPendingUpcoming = a.status === "Pending" && dateA >= now;
      const bPendingUpcoming = b.status === "Pending" && dateB >= now;

      if (aPendingUpcoming && !bPendingUpcoming) return -1;
      if (bPendingUpcoming && !aPendingUpcoming) return 1;

      // Second priority: All pending posts
      if (a.status === "Pending" && b.status !== "Pending") return -1;
      if (b.status === "Pending" && a.status !== "Pending") return 1;

      // Third priority: Upcoming posts (nearest first)
      if (dateA >= now && dateB >= now) {
        return dateA.getTime() - dateB.getTime();
      }

      // Fourth priority: Recent past posts (most recent first)
      if (dateA < now && dateB < now) {
        return dateB.getTime() - dateA.getTime();
      }

      // Mixed future/past: prioritize future
      if (dateA >= now && dateB < now) return -1;
      if (dateB >= now && dateA < now) return 1;

      // Final fallback: nearest to current time
      const diffA = Math.abs(dateA.getTime() - now.getTime());
      const diffB = Math.abs(dateB.getTime() - now.getTime());
      return diffA - diffB;
    });
  }, [socialMediaPosts, searchTerm]);

  const isEmpty = filteredData.length === 0;

  // Month Picker Modal (replacing Popover)
  const MonthPickerModal = () => (
    <Modal
      open={monthPickerOpen}
      onClose={handleMonthPickerClose}
      aria-labelledby="month-picker-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 3,
          minWidth: 380,
          maxWidth: 420,
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          outline: "none",
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
              pb: 2,
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}
              >
                Select Month
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Choose a month to view social media posts
              </Typography>
            </Box>

            {/* Close button and Current selection indicator */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  bgcolor: alpha("#3b82f6", 0.1),
                  borderRadius: 2,
                  border: "1px solid #3b82f6",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#3b82f6", fontWeight: 600 }}
                >
                  Current:{" "}
                  {availableMonths.find((m) => m.value === selectedMonth)
                    ?.short || "N/A"}
                </Typography>
              </Box>

              <IconButton
                onClick={handleMonthPickerClose}
                size="small"
                sx={{
                  color: "#64748b",
                  "&:hover": {
                    bgcolor: alpha("#64748b", 0.1),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Month Grid - 3 columns x 4 rows */}
          <Grid container spacing={1.5}>
            {availableMonths.map((month) => {
              const isSelected = tempSelectedMonth === month.value;
              const isCurrent = month.isCurrentMonth;
              const isCurrentlyActive = selectedMonth === month.value;

              return (
                <Grid item xs={4} key={month.value}>
                  <Button
                    fullWidth
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => handleMonthSelect(month.value)}
                    sx={{
                      py: 2,
                      px: 1,
                      borderRadius: 2,
                      fontSize: "0.875rem",
                      bgcolor: isSelected ? "#3b82f6" : "transparent",
                      color: isSelected ? "#fff" : "#374151",
                      borderColor: isSelected
                        ? "#3b82f6"
                        : isCurrentlyActive
                        ? "#10b981"
                        : "#e5e7eb",
                      position: "relative",
                      minHeight: 64,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        bgcolor: isSelected
                          ? "#2563eb"
                          : alpha("#3b82f6", 0.08),
                        borderColor: "#3b82f6",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                      },
                      "&:active": {
                        transform: "translateY(0px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {month.short}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.8,
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {month.year}
                      </Typography>

                      {/* Current month indicator */}
                      {isCurrent && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: isSelected ? "#fff" : "#10b981",
                            border: isSelected ? "none" : "2px solid #fff",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                      )}

                      {/* Currently active month indicator */}
                      {isCurrentlyActive && !isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#10b981",
                            border: "2px solid #fff",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                      )}

                      {/* Selection indicator */}
                      {isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 4,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 20,
                            height: 2,
                            borderRadius: 1,
                            bgcolor: "#fff",
                            opacity: 0.8,
                          }}
                        />
                      )}
                    </Box>
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          {/* Footer with actions */}
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#10b981",
                  }}
                />
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Current Month
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#10b981",
                    border: "2px solid #fff",
                  }}
                />
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Active Filter
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleCancelMonth}
                sx={{
                  borderColor: "#e5e7eb",
                  color: "#64748b",
                  "&:hover": {
                    borderColor: "#3b82f6",
                    color: "#3b82f6",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyMonth}
                disabled={tempSelectedMonth === selectedMonth}
                sx={{
                  bgcolor: "#3b82f6",
                  "&:hover": {
                    bgcolor: "#2563eb",
                  },
                  "&:disabled": {
                    bgcolor: "#94a3b8",
                    opacity: 0.6,
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>

          {/* Show selection change preview */}
          {tempSelectedMonth !== selectedMonth && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: alpha("#3b82f6", 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha("#3b82f6", 0.2)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#3b82f6", fontWeight: 600 }}
              >
                Will switch from{" "}
                {availableMonths.find((m) => m.value === selectedMonth)?.label}{" "}
                to{" "}
                {
                  availableMonths.find((m) => m.value === tempSelectedMonth)
                    ?.label
                }
              </Typography>
            </Box>
          )}

          {/* Instructions for users */}
          <Box
            sx={{
              mt: 2,
              p: 1,
              bgcolor: alpha("#64748b", 0.05),
              borderRadius: 1,
              border: `1px solid ${alpha("#64748b", 0.1)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontStyle: "italic" }}
            >
              💡 Select a month and click "Apply" to filter posts. Click "×" or
              "Cancel" to close.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  // Month Selector Component
  const MonthSelector = () => {
    const currentMonthData =
      availableMonths.find((m) => m.value === selectedMonth) ||
      availableMonths[0];

    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 2, md: 0 },
          bgcolor: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          borderRadius: "12px 12px 0 0",
        }}
      >
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" }, 
          gap: { xs: 2, sm: 3 },
          flex: 1,
        }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 1.5,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}>
            <Typography
              variant="h6"
              sx={{ 
                fontWeight: 700, 
                color: "#1e293b", 
                fontSize: { xs: "0.95rem", sm: "1.1rem" },
                minWidth: { xs: "auto", sm: "60px" },
              }}
            >
              Month:
            </Typography>

            <IconButton
              size="small"
              onClick={handlePrevMonth}
              disabled={currentIndex === 0}
              sx={{
                border: "2px solid #e5e7eb",
                borderRadius: 2,
                bgcolor: "#fff",
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                "&:hover": {
                  borderColor: "#3b82f6",
                  bgcolor: "#f8fafc",
                },
                "&:disabled": {
                  opacity: 0.4,
                  bgcolor: "#f8fafc",
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Button
              variant="outlined"
              onClick={handleMonthPickerOpen}
              endIcon={<ArrowDownIcon />}
              sx={{
                minWidth: { xs: 140, sm: 180 },
                height: { xs: 38, sm: 44 },
                justifyContent: "space-between",
                borderColor: "#d1d5db",
                borderWidth: 2,
                color: "#1e293b",
                bgcolor: "#fff",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                borderRadius: 2,
                px: { xs: 1.5, sm: 2 },
                "&:hover": {
                  borderColor: "#3b82f6",
                  bgcolor: "#f8fafc",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                },
              }}
            >
              {currentMonthData?.label || selectedMonth}
            </Button>

            <IconButton
              size="small"
              onClick={handleNextMonth}
              disabled={currentIndex === availableMonths.length - 1}
              sx={{
                border: "2px solid #e5e7eb",
                borderRadius: 2,
                bgcolor: "#fff",
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                "&:hover": {
                  borderColor: "#3b82f6",
                  bgcolor: "#f8fafc",
                },
                "&:disabled": {
                  opacity: 0.4,
                  bgcolor: "#f8fafc",
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Status indicators */}
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 2,
            flexWrap: "wrap",
          }}>
            {loadingSocialPosts && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", fontWeight: 500 }}
                >
                  Loading...
                </Typography>
              </Box>
            )}

            {apiError && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  bgcolor: "#fef2f2",
                  borderRadius: 1,
                  border: "1px solid #fecaca",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#dc2626", fontWeight: 500 }}
                >
                  Failed to load data
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Enhanced Refresh Button */}
        <Button
          variant="outlined"
          size="medium"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshPosts}
          disabled={loadingSocialPosts}
          sx={{
            borderRadius: 2,
            borderWidth: 2,
            height: { xs: 38, sm: 44 },
            px: { xs: 2, sm: 3 },
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.95rem" },
            borderColor: "#d1d5db",
            color: "#374151",
            minWidth: { xs: "auto", sm: "120px" },
            "&:hover": {
              borderColor: "#3b82f6",
              bgcolor: "#f8fafc",
              color: "#3b82f6",
            },
            "&:disabled": {
              opacity: 0.6,
            },
          }}
        >
          {isMobile ? "" : "Refresh"}
        </Button>
      </Box>
    );
  };

  // Loading State Component
  const LoadingState = () => (
    <Box sx={{ p: 8, textAlign: "center" }}>
      <CircularProgress sx={{ mb: 2 }} />
      <Typography variant="h6" color="text.secondary">
        Loading social media posts for {selectedMonth}...
      </Typography>
    </Box>
  );

  // Empty State Component
  const EmptyState = () => (
    <Box sx={{ p: 8, textAlign: "center" }}>
      <Typography variant="h6" color="text.secondary" paragraph>
        No social media posts found for {selectedMonth}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {searchTerm
          ? `No results match "${searchTerm}" in ${selectedMonth}. Try adjusting your search criteria or selecting a different month.`
          : `There are no social media posts scheduled for ${selectedMonth}.`}
      </Typography>
      {searchTerm ? (
        <Button
          variant="outlined"
          onClick={onClearSearch}
          sx={{ borderRadius: 2, mr: 2 }}
        >
          Clear Search
        </Button>
      ) : null}
      <Button
        variant="outlined"
        onClick={handleRefreshPosts}
        startIcon={<RefreshIcon />}
        sx={{ borderRadius: 2 }}
      >
        Refresh Posts
      </Button>
    </Box>
  );

  // Transform API data to table format
  const transformedData = useMemo(() => {
    return socialMediaPosts.map((post) => {
      const scheduledDateTime = new Date(post.scheduleDateTime);

      return {
        id: post.postId,
        content: post.postContent,
        platforms: post.platforms || [],
        accountNames:
          post.accounts
            ?.map((acc) => acc.accountName || acc.accountId)
            .join(", ") || "",
        status: post.approvalStatus || "Pending",
        scheduledFor: scheduledDateTime.toLocaleString("en-US", {
          dateStyle: "medium",
          timeStyle: "short",
        }),
        scheduledDateTime: scheduledDateTime,
        rawScheduleDateTime: post.scheduleDateTime, // ADDED: Keep original UTC string
        requestedBy: post.createdBy || "System",
        subject: post.postContent?.substring(0, 50) + "...",
        message: post.postContent,
        contentType: post.contentType || "Post",
        type: post.contentType || "Social Media Post",
        imageUrl: post.mediaUrls?.[0] || null,
        mediaFileCount: post.mediaFileCount || 0,
        hashtags: extractHashtags(post.postContent),
      };
    });
  }, [socialMediaPosts]);

  // Enhanced Data Table Component with time indicators
  const DataTable = () => {
    const now = new Date();

    // Helper function to get time status
    const getTimeStatus = (scheduledDateTime) => {
      const timeDiff = scheduledDateTime.getTime() - now.getTime();
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

      if (timeDiff < 0) {
        return {
          status: "past",
          label: "Posted",
          color: "#6b7280",
          bgColor: alpha("#6b7280", 0.1),
        };
      } else if (hoursDiff < 1) {
        return {
          status: "urgent",
          label: "Very Soon",
          color: "#dc2626",
          bgColor: alpha("#dc2626", 0.1),
        };
      } else if (hoursDiff < 24) {
        return {
          status: "today",
          label: "Today",
          color: "#f59e0b",
          bgColor: alpha("#f59e0b", 0.1),
        };
      } else if (daysDiff < 2) {
        return {
          status: "tomorrow",
          label: "Tomorrow",
          color: "#10b981",
          bgColor: alpha("#10b981", 0.1),
        };
      } else if (daysDiff < 7) {
        return {
          status: "thisweek",
          label: "This Week",
          color: "#3b82f6",
          bgColor: alpha("#3b82f6", 0.1),
        };
      } else {
        return {
          status: "future",
          label: "Future",
          color: "#8b5cf6",
          bgColor: alpha("#8b5cf6", 0.1),
        };
      }
    };

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#374151", py: 2 }}>
                  Content / Message
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#374151", py: 2 }}>
                  Platforms
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#374151", py: 2 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#374151", py: 2 }}>
                  Scheduled
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#374151", py: 2 }}>
                  Media
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#374151",
                    py: 2,
                    textAlign: "right",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => {
                  const timeStatus = getTimeStatus(item.scheduledDateTime);
                  const isHighPriority =
                    item.status === "Pending" &&
                    (timeStatus.status === "urgent" ||
                      timeStatus.status === "today" ||
                      timeStatus.status === "tomorrow");

                  // UPDATED: Use rawScheduleDateTime for validation
                  const canApprove =
                    userPermissions.write &&
                    item.status === "Pending" &&
                    isApprovalTimeValid(item.rawScheduleDateTime);

                  return (
                    <TableRow
                      key={item.id}
                      sx={{
                        "&:hover": { bgcolor: alpha("#3b82f6", 0.02) },
                        borderBottom: "1px solid #f1f5f9",
                        bgcolor: isHighPriority
                          ? alpha("#fef3c7", 0.3)
                          : "transparent",
                        borderLeft: isHighPriority
                          ? "4px solid #f59e0b"
                          : "4px solid transparent",
                      }}
                    >
                      <TableCell sx={{ py: 2, maxWidth: 300 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          {/* Priority indicator */}
                          {isHighPriority && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#f59e0b",
                                mt: 1,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                color: "#374151",
                                fontWeight: 500,
                                mb: 1,
                              }}
                            >
                              {item.content}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b" }}
                            >
                              ID: {item.id} • {item.contentType || item.type}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {item.platforms?.map((platform, index) => (
                            <Chip
                              key={index}
                              label={platform}
                              size="small"
                              sx={{
                                bgcolor: alpha("#3b82f6", 0.1),
                                color: "#3b82f6",
                                borderRadius: 1,
                                fontSize: "0.75rem",
                                height: 20,
                              }}
                            />
                          ))}
                        </Box>
                        {item.accountNames && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748b", mt: 0.5, display: "block" }}
                          >
                            {item.accountNames}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={item.status}
                          size="small"
                          sx={{
                            ...getStatusColor(item.status),
                            borderRadius: 1,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            height: 24,
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mb: 0.5,
                            }}
                          >
                            <TimeIcon sx={{ fontSize: 16, color: "#64748b" }} />
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b" }}
                            >
                              {item.scheduledFor}
                            </Typography>
                          </Box>

                          {/* Time status indicator */}
                          <Chip
                            label={timeStatus.label}
                            size="small"
                            sx={{
                              bgcolor: timeStatus.bgColor,
                              color: timeStatus.color,
                              fontSize: "0.7rem",
                              height: 18,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {item.imageUrl && (
                            <Box
                              component="img"
                              src={item.imageUrl}
                              alt="Media preview"
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                objectFit: "cover",
                                border: "1px solid #e5e7eb",
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748b" }}
                          >
                            {item.mediaFileCount || 0} file(s)
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* UPDATED: Actions cell */}
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          {canApprove && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuickApprove(item);
                                }}
                                sx={{
                                  color: "#059669",
                                  "&:hover": { bgcolor: alpha("#059669", 0.1) },
                                }}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!canApprove &&
                            userPermissions.write &&
                            item.status === "Pending" && (
                              <Tooltip
                                title={getTimeValidationMessage(
                                  item.rawScheduleDateTime
                                )}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled
                                    sx={{ color: "#dc2626", opacity: 0.4 }}
                                  >
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          {userPermissions.write &&
                            item.status === "Pending" && (
                              <Tooltip title="Decline">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickReject(item);
                                  }}
                                  sx={{
                                    color: "#dc2626",
                                    "&:hover": {
                                      bgcolor: alpha("#dc2626", 0.1),
                                    },
                                  }}
                                >
                                  <DeclineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewClick(item);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: "1px solid #e5e7eb",
            bgcolor: "#fafbfc",
          }}
        />
      </>
    );
  };

  // Add useEffect to calculate and send stats whenever data changes
  useEffect(() => {
    const calculateStats = () => {
      const pendingCount = socialMediaPosts.filter(
        (post) => post.status === "Pending"
      ).length;

      // Calculate approved today count
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const approvedTodayCount = socialMediaPosts.filter((post) => {
        // Check if post was approved today based on updatedAt or createdAt
        const updatedDate = new Date(post.updatedAt || post.createdAt);
        return (
          post.status === "Approved" &&
          updatedDate >= today &&
          updatedDate < tomorrow
        );
      }).length;

      console.log("Stats calculated:", {
        pendingCount,
        approvedTodayCount,
        totalPosts: socialMediaPosts.length,
      });

      // Send stats to parent component
      if (onStatsUpdate) {
        onStatsUpdate({
          pendingCount,
          approvedTodayCount,
          totalCount: socialMediaPosts.length,
        });
      }
    };

    calculateStats();
  }, [socialMediaPosts, onStatsUpdate]);

  // Main render logic
  return (
    <>
      <MonthSelector />
      <MonthPickerModal />

      {/* Post Preview Modal */}
      <PostPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        post={selectedPost}
        onPostUpdated={handlePostUpdated}
        userPermissions={userPermissions}
        isApprovalTimeValid={isApprovalTimeValid}
        getTimeValidationMessage={getTimeValidationMessage}
      />

      {loadingSocialPosts ? (
        <LoadingState />
      ) : isEmpty ? (
        <EmptyState />
      ) : (
        <DataTable />
      )}
    </>
  );
};

export default SocialMediaApprovals;
